'use client'

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CategoryGroupConfig } from '@/lib/blog/category-groups'

interface BlogCategory {
  id: string
  slug: string
  name: string
}

interface Props {
  categories: BlogCategory[]
  categoryCountsObj: Record<string, number>
  total: number
  /** slug da categoria ativa (vindo do query param), ou undefined */
  active?: string
  /** Grupos hierárquicos (pode ser vazio: então renderiza flat) */
  groups: CategoryGroupConfig[]
}

function CaretIcon() {
  return (
    <svg
      className="caret"
      viewBox="0 0 24 24"
      width="10"
      height="10"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

export function BlogFilterBar({ categories, categoryCountsObj, total, active, groups }: Props) {
  const categoryCounts = new Map(Object.entries(categoryCountsObj))
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)

  // Fecha dropdowns ao clicar fora ou apertar ESC
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setOpenGroup(null)
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenGroup(null)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  const toggle = useCallback((key: string) => {
    setOpenGroup(prev => (prev === key ? null : key))
  }, [])

  const catBySlug = new Map(categories.map(c => [c.slug, c]))
  const inGroupSlugs = new Set<string>()
  groups.forEach(g => {
    inGroupSlugs.add(g.parentSlug)
    g.childSlugs.forEach(s => inGroupSlugs.add(s))
  })
  const ungrouped = categories.filter(c => !inGroupSlugs.has(c.slug))

  return (
    <nav
      className="filter-bar"
      aria-label="Filtrar por categoria"
      ref={containerRef as React.RefObject<HTMLElement>}
    >
      <div className="filter-inner">
        <Link href="/blog" className={`filter-chip${!active ? ' is-active' : ''}`}>
          Tudo <span className="filter-chip-count">{total}</span>
        </Link>

        {groups.map(group => {
          const parentCat = catBySlug.get(group.parentSlug)
          const parentLabel = parentCat?.name || group.label
          const parentCount = parentCat ? categoryCounts.get(group.parentSlug) || 0 : 0
          const isParentActive = active === group.parentSlug
          const isChildActive = group.childSlugs.some(s => active === s)
          const isAnyActive = isParentActive || isChildActive
          const isOpen = openGroup === group.parentSlug
          const isVirtual = !parentCat

          return (
            <div
              key={group.parentSlug}
              className={`filter-group${isOpen ? ' is-open' : ''}${isAnyActive ? ' has-active' : ''}`}
            >
              {isVirtual ? (
                <button
                  type="button"
                  className="filter-chip filter-chip-parent"
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(group.parentSlug)
                  }}
                >
                  <span style={{ fontSize: 14, lineHeight: 1 }}>●</span>
                  {parentLabel}
                  <CaretIcon />
                </button>
              ) : (
                <button
                  type="button"
                  className={`filter-chip filter-chip-parent${isParentActive ? ' is-active' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(group.parentSlug)
                  }}
                >
                  <span
                    style={{ color: `var(--cat-${parentCat!.slug})`, fontSize: 14, lineHeight: 1 }}
                  >
                    ●
                  </span>
                  {parentLabel}
                  {parentCount > 0 && (
                    <span className="filter-chip-count">{parentCount}</span>
                  )}
                  <CaretIcon />
                </button>
              )}

              <div className="filter-dropdown" role="menu">
                {!isVirtual && (
                  <Link
                    href={`/blog?category=${parentCat!.slug}`}
                    className={`filter-chip${isParentActive ? ' is-active' : ''}`}
                    onClick={() => setOpenGroup(null)}
                    role="menuitem"
                  >
                    <span style={{ color: `var(--cat-${parentCat!.slug})`, fontSize: 14, lineHeight: 1 }}>●</span>
                    Ver tudo de {parentLabel}
                    {parentCount > 0 && <span className="filter-chip-count">{parentCount}</span>}
                  </Link>
                )}
                {group.childSlugs.map(slug => {
                  const cat = catBySlug.get(slug)
                  if (!cat) return null
                  const count = categoryCounts.get(cat.slug) || 0
                  return (
                    <Link
                      key={cat.id}
                      href={`/blog?category=${cat.slug}`}
                      className={`filter-chip${active === cat.slug ? ' is-active' : ''}`}
                      onClick={() => setOpenGroup(null)}
                      role="menuitem"
                    >
                      <span
                        style={{ color: `var(--cat-${cat.slug})`, fontSize: 14, lineHeight: 1 }}
                      >
                        ●
                      </span>
                      {cat.name}
                      {count > 0 && <span className="filter-chip-count">{count}</span>}
                    </Link>
                  )
                })}
              </div>
            </div>
          )
        })}

        {ungrouped.map(cat => {
          const count = categoryCounts.get(cat.slug) || 0
          return (
            <Link
              key={cat.id}
              href={`/blog?category=${cat.slug}`}
              className={`filter-chip${active === cat.slug ? ' is-active' : ''}`}
            >
              <span style={{ color: `var(--cat-${cat.slug})`, fontSize: 14, lineHeight: 1 }}>●</span>
              {cat.name}
              {count > 0 && <span className="filter-chip-count">{count}</span>}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
